import React, { useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import { styled } from '@mui/material/styles';
import { Button, Grid, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import initialValues from '../../validation/careers/initialValues';
import validationSchema from '../../validation/careers/validationSchema';
import formModel from '../../validation/careers/formModel';
import InputField from './form-fields/InputField';
import AxiosRequest from '../../utils/axios/AxiosRequest';
import UploadInput from './form-fields/UploadInput';
import UploadClaim from './form-fields/UploadClaimHistory';
import { APP_ALLOWED_FILE_TYPES } from '../../constants/AppConstants';
import RecaptchaText from '../RecaptchaText';
import RecaptchaField from '../recaptcha/RecaptchaField';
import FormMessage from './FormMessage';

const ExistingPolicyScheduleFormRoot = styled('div')(({ theme }) => ({
    margin: '24px 0 0',
}));

const ExistingPolicyScheduleFormContainer = styled('div')(({ theme }) => ({
    padding: '0 54px',
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
    marginTop: '10px',
    color: theme.palette.error.main,
}));

const ExistingPolicyScheduleForm = () => {
    const { firstName, lastName, email } = formModel;
    const [activeValidation, setActiveValidation] = useState(false);
    const [attachment, setAttachment] = useState(null);
    const [attachmentError, setAttachmentError] = useState(null);
    const [claimHistory, setClaimHistory] = useState(null);
    const [claimHistoryError, setClaimHistoryError] = useState(null);
    const [formResponse, setFormResponse] = useState(null);
    const recaptchaRef = useRef();

    const submit = async (values, actions) => {
        const timeout = setTimeout(() => {
            setFormResponse({
                isSuccessful: false,
                message: 'Your submission could not be sent. Please try again later.',
            });
            setActiveValidation(false);
            actions.setSubmitting(false);
            return;
        }, 60000);
    

        if (!attachment) {
            setAttachmentError('File is required');
            return;
        }
    
        const token = await recaptchaRef.current.executeAsync();
    
        const getFormData = (object) =>
            Object.keys(object).reduce((formData, key) => {
                formData.append(key, object[key]);
                return formData;
            }, new FormData());
    
        var uploadData = getFormData({
            ...values,
            file: attachment, 
            claimHistoryFile: claimHistory ?? null, 
            token,
        });
    
        var result = await AxiosRequest.post(`api/forms/existingpolicyschedule`, uploadData);
    
        clearTimeout(timeout);

        setFormResponse(result);
        setActiveValidation(false);
        actions.setSubmitting(false);
        actions.resetForm();
    };
    
    const handleFileChange = (event, setFile, setError, fileType) => {
        const file = event.target.files[0] || null;
    
        if (validateFile(file, setError, fileType)) {
            setFile(file);
        } else {
            setFile(null);
        }
    
        if (fileType === 'Claim History' && !file) {
            setError(null);
        }
    };
    
    const validateFile = (file, setError, fileType) => {
        if (file === null) {
            setError(null); 
            return true;
        }
    
        if (!file) {
            setError(`${fileType} is required`);
            return false;
        }

        if (
            !APP_ALLOWED_FILE_TYPES.includes(file.type) &&
            !APP_ALLOWED_FILE_TYPES.includes(file.name.split('.').pop())
        ) {
            const fileTypeStr = file.type || `.${file.name.split('.').pop()}`;
            setError(`${fileType} type ${fileTypeStr} not allowed.`);
            return false;
        }

        let maxFileSize = 5242880;
        if (file.size >= maxFileSize) { 
            
            setError(`The ${fileType} file you uploaded exceeds the maximum allowed size of ${maxFileSize/1024/1024} MB. Please upload a smaller file.`);    
            return false;
        }

        setError(null);  
        return true;
    };

    return (
        <ExistingPolicyScheduleFormRoot>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnChange={activeValidation}
                validateOnBlur={activeValidation}
                onSubmit={(values, actions) => {
                    submit(values, actions);
                }}
            >
                {({ isSubmitting }) => (
                    <Form>
                        <RecaptchaField recaptchaRef={recaptchaRef} />

                        <ExistingPolicyScheduleFormContainer>
                            <FormMessage response={formResponse} />

                            <Grid container spacing={5}>
                                <Grid item xs={12} md={4}>
                                    <InputField
                                        name={firstName.name}
                                        label={firstName.label}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <InputField
                                        name={lastName.name}
                                        label={lastName.label}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <InputField name={email.name} label={email.label} fullWidth />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body1">
                                        <b>Upload Schedule (Word, PDF, PNG, JPG, or JPEG only)*</b>
                                    </Typography>

                                    <UploadInput
                                        name="scheduleFile"
                                        label="File"
                                        onChange={(e) =>
                                            handleFileChange(e, setAttachment, setAttachmentError, 'Schedule')
                                        }
                                    />
                                    <Typography variant="h6" marginTop={'10px'}>
                                        {attachment && 'name' in attachment && attachment.name}
                                    </Typography>

                                    <ErrorMessage variant="h6">
                                        {attachmentError && attachmentError}
                                    </ErrorMessage>
                                </Grid>

                                <Grid item xs={12}>
                                <Typography variant="body1">
                                    <b>Upload Claims History (Word, PDF, PNG, JPG, or JPEG only)</b> 
                                    <span style={{ fontStyle: 'italic', color: 'gray' }}> Optional</span>
                                </Typography>
                                    <UploadClaim
                                        name="claimHistoryFile"
                                        label="Claim History"
                                        onChange={(e) =>
                                            handleFileChange(e, setClaimHistory, setClaimHistoryError, 'Claim History')
                                        }
                                    />
                                    <Typography variant="h6" marginTop={'10px'}>
                                        {claimHistory && 'name' in claimHistory && claimHistory.name}
                                    </Typography>

                                    <ErrorMessage variant="h6">
                                        {claimHistoryError && claimHistoryError}
                                    </ErrorMessage>
                                </Grid>

                                <Grid item xs={12} style={{ display: 'flex' }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        style={{ margin: '0 auto' }}
                                        onClick={() => {
                                            recaptchaRef.current.reset();
                                            validateFile();
                                            setActiveValidation(true);
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <CircularProgress
                                                color="secondary"
                                                sx={{
                                                    color: '#FFFFFF',
                                                }}
                                                size={20}
                                            />
                                        ) : (
                                            'Submit'
                                        )}
                                    </Button>
                                </Grid>
                            </Grid>

                            <RecaptchaText />
                        </ExistingPolicyScheduleFormContainer>
                    </Form>
                )}
            </Formik>
        </ExistingPolicyScheduleFormRoot>
    );
};

export default ExistingPolicyScheduleForm;
